import React from "react";
import {
	Box,
	Card,
	CardContent,
	Typography,
	Chip,
	Divider,
	CircularProgress,
	Alert
} from "@mui/material";
import { RichTextReadOnly } from "mui-tiptap";
import { getExtensions } from "../UI/HtmlEditor";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { useProjectBrief } from "../../hooks/useProject";
import { useEnumTranslation } from "../../utils/translateEnum";
import { formatCurrency, formatDate } from "../../utils/formatting";

interface ProjectBriefViewProps {
	projectId: number;
}

const ProjectBriefView: React.FC<ProjectBriefViewProps> = ({ projectId }) => {
	const { t } = useTranslation();
	const { data: projectBrief, isLoading, error } = useProjectBrief(projectId);
	const {
		translateProjectDomain,
		translateMissionType,
		translateProjectType,
		translateProjectLocation,
		translateDurationType
	} = useEnumTranslation();

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" p={3}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" sx={{ m: 2 }}>
				{t("project.brief.loadError")}
			</Alert>
		);
	}

	if (!projectBrief) {
		return (
			<Alert severity="info" sx={{ m: 2 }}>
				{t("project.brief.notFound")}
			</Alert>
		);
	}

	return (
		<Box p={3}>
			<Typography variant="h5" gutterBottom>
				{t("project.brief.title")}
			</Typography>

			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				{t("project.brief.subtitle", {
					date: formatDate(projectBrief.createdAt)
				})}
			</Typography>

			<Card>
				<CardContent>
					<Grid container spacing={3}>
						{/* Basic Information */}
						<Grid size={12}>
							<Typography variant="h6" gutterBottom>
								{t("project.basicInformation")}
							</Typography>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<Typography variant="subtitle2" color="text.secondary">
								{t("project.name")}
							</Typography>
							<Typography variant="body1" sx={{ mb: 2 }}>
								{projectBrief.name}
							</Typography>
						</Grid>

						{projectBrief.domain && (
							<Grid size={{ xs: 12, md: 6 }}>
								<Typography variant="subtitle2" color="text.secondary">
									{t("project.domain")}
								</Typography>
								<Chip
									label={translateProjectDomain(projectBrief.domain)}
									sx={{ mb: 2 }}
								/>
							</Grid>
						)}

						{projectBrief.missionType && (
							<Grid size={{ xs: 12, md: 6 }}>
								<Typography variant="subtitle2" color="text.secondary">
									{t("project.missionType")}
								</Typography>
								<Chip
									label={translateMissionType(projectBrief.missionType)}
									sx={{ mb: 2 }}
								/>
							</Grid>
						)}

						<Grid size={{ xs: 12, md: 6 }}>
							<Typography variant="subtitle2" color="text.secondary">
								{t("project.type")}
							</Typography>
							<Chip label={translateProjectType(projectBrief.type)} sx={{ mb: 2 }} />
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<Typography variant="subtitle2" color="text.secondary">
								{t("project.location")}
							</Typography>
							<Chip
								label={translateProjectLocation(projectBrief.location)}
								sx={{ mb: 2 }}
							/>
						</Grid>

						{projectBrief.company && (
							<Grid size={12}>
								<Typography variant="subtitle2" color="text.secondary">
									{t("project.company")}
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{projectBrief.company.legalName}
								</Typography>
							</Grid>
						)}

						{projectBrief.createdBy && (
							<Grid size={12}>
								<Typography variant="subtitle2" color="text.secondary">
									{t("project.createdBy")}
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{projectBrief.createdBy.firstName} {projectBrief.createdBy.lastName}
								</Typography>
							</Grid>
						)}

						<Grid size={12}>
							<Divider sx={{ my: 2 }} />
						</Grid>

						{/* Project Description */}
						<Grid size={12}>
							<Typography variant="h6" gutterBottom>
								{t("project.projectDescription")}
							</Typography>
						</Grid>

						<Grid size={12}>
							<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
								{t("project.objectives")}
							</Typography>
							<Box sx={{ mb: 2 }}>
								<RichTextReadOnly
									content={projectBrief.objectives}
									extensions={getExtensions()}
								/>
							</Box>
						</Grid>

						<Grid size={12}>
							<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
								{t("project.presentationAndNeeds")}
							</Typography>
							<Box sx={{ mb: 2 }}>
								<RichTextReadOnly
									content={projectBrief.presentationAndNeeds}
									extensions={getExtensions()}
								/>
							</Box>
						</Grid>

						<Grid size={12}>
							<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
								{t("project.hardSkillsRequired")}
							</Typography>
							<Box sx={{ mb: 2 }}>
								<RichTextReadOnly
									content={projectBrief.talentHardSkills}
									extensions={getExtensions()}
								/>
							</Box>
						</Grid>

						<Grid size={12}>
							<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
								{t("project.softSkillsRequired")}
							</Typography>
							<Box sx={{ mb: 2 }}>
								<RichTextReadOnly
									content={projectBrief.talentSoftSkills}
									extensions={getExtensions()}
								/>
							</Box>
						</Grid>

						<Grid size={12}>
							<Divider sx={{ my: 2 }} />
						</Grid>

						{/* Budget & Timeline */}
						<Grid size={12}>
							<Typography variant="h6" gutterBottom>
								{t("project.budgetAndLocation")}
							</Typography>
						</Grid>

						{projectBrief.budget && (
							<Grid size={{ xs: 12, md: 6 }}>
								<Typography variant="subtitle2" color="text.secondary">
									{t("project.budget")}
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{formatCurrency(projectBrief.budget)}
								</Typography>
							</Grid>
						)}

						{projectBrief.dailyRate && (
							<Grid size={{ xs: 12, md: 6 }}>
								<Typography variant="subtitle2" color="text.secondary">
									{t("project.dailyRate")}
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{formatCurrency(projectBrief.dailyRate)}
								</Typography>
							</Grid>
						)}

						{projectBrief.duration && (
							<Grid size={{ xs: 12, md: 6 }}>
								<Typography variant="subtitle2" color="text.secondary">
									{t("project.duration")}
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{projectBrief.duration}{" "}
									{projectBrief.durationType
										? translateDurationType(projectBrief.durationType)
										: ""}
								</Typography>
							</Grid>
						)}

						{projectBrief.desiredStartDate && (
							<Grid size={{ xs: 12, md: 6 }}>
								<Typography variant="subtitle2" color="text.secondary">
									{t("project.desiredStartDate")}
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{formatDate(projectBrief.desiredStartDate)}
								</Typography>
							</Grid>
						)}

						{/* Additional Details */}
						{((projectBrief.type === 'TimeAndMaterial' && (projectBrief.fullTime !== undefined || projectBrief.daysPerPeriod)) ||
							projectBrief.hybridDetails ||
							projectBrief.teamIntegration !== undefined ||
							projectBrief.feePercentage) && (
							<>
								<Grid size={12}>
									<Divider sx={{ my: 2 }} />
								</Grid>

								<Grid size={12}>
									<Typography variant="h6" gutterBottom>
										{t("project.brief.additionalDetails")}
									</Typography>
								</Grid>

								{projectBrief.type === 'TimeAndMaterial' && projectBrief.fullTime !== undefined && (
									<Grid size={{ xs: 12, md: 6 }}>
										<Typography variant="subtitle2" color="text.secondary">
											{t("project.fullTime")}
										</Typography>
										<Chip
											label={projectBrief.fullTime ? t("common.yes") : t("common.no")}
											color={projectBrief.fullTime ? "success" : "default"}
											sx={{ mb: 2 }}
										/>
									</Grid>
								)}

								{projectBrief.type === 'TimeAndMaterial' && projectBrief.daysPerPeriod && (
									<Grid size={{ xs: 12, md: 6 }}>
										<Typography variant="subtitle2" color="text.secondary">
											{t("project.daysPerPeriod")}
										</Typography>
										<Typography variant="body1" sx={{ mb: 2 }}>
											{projectBrief.daysPerPeriod}
										</Typography>
									</Grid>
								)}

								{projectBrief.teamIntegration !== undefined && (
									<Grid size={{ xs: 12, md: 6 }}>
										<Typography variant="subtitle2" color="text.secondary">
											{t("project.teamIntegration")}
										</Typography>
										<Chip
											label={
												projectBrief.teamIntegration ? t("common.yes") : t("common.no")
											}
											color={projectBrief.teamIntegration ? "success" : "default"}
											sx={{ mb: 2 }}
										/>
									</Grid>
								)}

								{projectBrief.hybridDetails && (
									<Grid size={12}>
										<Typography variant="subtitle2" color="text.secondary">
											{t("project.hybridDetails")}
										</Typography>
										<Typography variant="body1" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
											{projectBrief.hybridDetails}
										</Typography>
									</Grid>
								)}

								{projectBrief.feePercentage && (
									<Grid size={{ xs: 12, md: 6 }}>
										<Typography variant="subtitle2" color="text.secondary">
											{t("project.feePercentage")}
										</Typography>
										<Typography variant="body1" sx={{ mb: 2 }}>
											{projectBrief.feePercentage}%
										</Typography>
									</Grid>
								)}
							</>
						)}
					</Grid>
				</CardContent>
			</Card>
		</Box>
	);
};

export default ProjectBriefView;
